import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../integrationQA/utils/apiClient";
import MetricCard from "./MetricCard";
import TrendChart from "./TrendChart";

const metrics = [
  {
    id: "engagement-overview",
    label: "Engagement Overview",
    value: "1,250",
    helper: "Total messages",
    icon: "bi-chat-left-text-fill",
    colorClass: "blue",
    change: "+12.5%",
    changeType: "up",
    description: "Overall visitor engagement measured through conversations and messages."
  },
  {
    id: "widget-opens",
    label: "Widget Opens",
    value: "3,480",
    helper: "Widget opens",
    icon: "bi-window",
    colorClass: "cyan",
    change: "+8.2%",
    changeType: "up",
    description: "Number of times visitors opened the ChatBizAI support widget."
  },
  {
    id: "conversations-started",
    label: "Conversations Started",
    value: "925",
    helper: "Started conversations",
    icon: "bi-chat-dots-fill",
    colorClass: "purple",
    change: "+10.4%",
    changeType: "up",
    description: "New conversations initiated by visitors during the selected period."
  },
  {
    id: "messages-session",
    label: "Messages / Session",
    value: "6.8",
    helper: "Messages per session",
    icon: "bi-envelope-fill",
    colorClass: "orange",
    change: "+4.1%",
    changeType: "up",
    description: "Average number of messages exchanged during a visitor session."
  },
  {
    id: "returning-visitors",
    label: "Returning Visitors",
    value: "38%",
    helper: "Returning visitors",
    icon: "bi-arrow-repeat",
    colorClass: "green",
    change: "+6.7%",
    changeType: "up",
    description: "Percentage of visitors who returned after an earlier visit."
  },
  {
    id: "ai-resolution-rate",
    label: "AI Resolution Rate",
    value: "92%",
    helper: "Resolution rate",
    icon: "bi-check-circle-fill",
    colorClass: "green",
    change: "+3.2%",
    changeType: "up",
    description: "Percentage of conversations resolved by AI without human handover."
  },
  {
    id: "escalation-rate",
    label: "Escalation Rate",
    value: "8%",
    helper: "Escalated conversations",
    icon: "bi-person-up",
    colorClass: "red",
    change: "-2.1%",
    changeType: "up",
    description: "Percentage of conversations escalated from AI to a human agent."
  },
  {
    id: "response-time",
    label: "Response Time",
    value: "18s",
    helper: "Avg. response time",
    icon: "bi-clock-fill",
    colorClass: "blue",
    change: "-5.3%",
    changeType: "up",
    description: "Average time taken by the AI or support workflow to respond."
  },
  {
    id: "unresolved-questions",
    label: "Unresolved Questions",
    value: "42",
    helper: "Unresolved questions",
    icon: "bi-question-circle-fill",
    colorClass: "red",
    change: "-9.4%",
    changeType: "up",
    description: "Questions that did not receive a satisfactory or resolved answer."
  },
  {
    id: "cta",
    label: "CTA Impressions / Clicks",
    value: "2,140 / 386",
    helper: "18% click-through rate",
    icon: "bi-cursor-fill",
    colorClass: "cyan",
    change: "+7.8%",
    changeType: "up",
    description: "Call-to-action impressions, clicks and click-through performance."
  },
  {
    id: "lead-conversion",
    label: "Lead Capture / Conversion",
    value: "148 / 23%",
    helper: "Captured leads / conversion rate",
    icon: "bi-person-check-fill",
    colorClass: "purple",
    change: "+5.6%",
    changeType: "up",
    description: "Leads captured through conversations and their conversion rate."
  }
];

export default function AnalyticsOverview({ onMetricClick, reportMode = false, range: externalRange, onRangeChange }) {
  const [range, setRange] = useState(externalRange || "7");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (externalRange && externalRange !== range) setRange(externalRange); }, [externalRange]);
  const changeRange = (value) => { setRange(value); if (onRangeChange) onRangeChange(value); };
  const [refreshKey, setRefreshKey] = useState(0);
  const [scenario, setScenario] = useState("");
  const [loadState, setLoadState] = useState("loading");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    setLoadState("loading");
    setLoadError("");
    const scenarioQuery = scenario ? `&mock=${encodeURIComponent(scenario)}` : "";
    apiClient(`/api/analytics/summary?range=${range}${scenarioQuery}`)
      .then((response) => {
        if (!active) return;
        if (!response?.data) {
          setAnalyticsData(null);
          setLoadState("empty");
          return;
        }
        setAnalyticsData(response.data);
        setLoadState("success");
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(error?.message || "Unable to load analytics data.");
        setLoadState("error");
      });
    return () => { active = false; };
  }, [range, refreshKey, scenario]);

  const metricValues = useMemo(() => {
    if (!analyticsData) return {};
    const engagement = analyticsData.engagement || {};
    const cta = analyticsData.cta || {};
    const lead = analyticsData.leadConversion || {};
    return {
      "engagement-overview": { value: Number(engagement.totalMessages || 0).toLocaleString(), helper: `${engagement.totalSessions || 0} sessions · ${engagement.uniqueVisitors || 0} visitors` },
      "widget-opens": { value: Number(analyticsData.widgetOpens || 0).toLocaleString(), helper: "Widget opens" },
      "conversations-started": { value: Number(analyticsData.conversationsStarted || 0).toLocaleString(), helper: "Started conversations" },
      "messages-session": { value: Number(analyticsData.messagesPerSession || 0).toFixed(1), helper: "Messages per session" },
      "returning-visitors": { value: `${analyticsData.returningVisitorsRate || 0}%`, helper: "Returning visitors" },
      "ai-resolution-rate": { value: `${analyticsData.aiResolutionRate || 0}%`, helper: "Resolution rate" },
      "escalation-rate": { value: `${analyticsData.escalationRate || 0}%`, helper: "Escalated conversations" },
      "response-time": { value: `${analyticsData.averageResponseTime || 0}s`, helper: "Avg. response time" },
      "unresolved-questions": { value: Number(analyticsData.unresolvedQuestions || 0).toLocaleString(), helper: "Unresolved questions" },
      cta: { value: `${Number(cta.impressions || 0).toLocaleString()} / ${Number(cta.clicks || 0).toLocaleString()}`, helper: `${cta.clickThroughRate || 0}% click-through rate` },
      "lead-conversion": { value: `${Number(lead.capturedLeads || 0).toLocaleString()} / ${lead.conversionRate || 0}%`, helper: `${lead.convertedLeads || 0} converted leads` }
    };
  }, [analyticsData]);

  const visibleMetrics = useMemo(() => {
    const hydrated = metrics.map((metric) => ({ ...metric, ...(metricValues[metric.id] || {}) }));
    return reportMode ? hydrated.slice(0, 6) : hydrated;
  }, [reportMode, metricValues]);

  const refreshData = () => { setScenario(""); setRefreshKey((value) => value + 1); };
  const downloadReport = () => {
    const rows = [["Date Range", "Metric", "Value", "Description"], ...metrics.map((metric) => {
      const value = metricValues[metric.id] || metric;
      return [range + " days", metric.label, value.value, value.helper];
    })];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `chatbizz-analytics-${range}d.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="analytics-page">
      <header className="page-header">
        <div>
          <div className="breadcrumb-text">Analytics &amp; Reports</div>
          <h1>{reportMode ? "Reports" : "Analytics Overview"}</h1>
          <p>Track conversations, engagement, AI performance and customer support metrics.</p>
        </div>

        <div className="header-actions">
          <button className="icon-button" title="Notifications">
            <i className="bi bi-bell"></i>
          </button>
          <select
            className="date-select"
            value={range}
            onChange={(e) => changeRange(e.target.value)}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <button className="refresh-btn" onClick={refreshData} disabled={loadState === "loading"}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={downloadReport} disabled={loadState !== "success"}>
            <i className="bi bi-download me-1"></i> Export CSV
          </button>
        </div>
      </header>

      <div className="filter-strip">
        <div className="filter-left">
          <span className="filter-title">Date Range</span>
          <span className="date-pill">Last {range} days</span>
        </div>

        <div className="state-buttons">
          <button className="state-btn" onClick={() => setScenario(scenario === "empty" ? "" : "empty")} aria-pressed={scenario === "empty"}>
            Empty
          </button>
          <button className="state-btn" onClick={() => setScenario(scenario === "error" ? "" : "error")} aria-pressed={scenario === "error"}>
            Error
          </button>
        </div>
      </div>

      {loadState === "error" && (
        <div className="state-alert error" role="alert">
          <i className="bi bi-exclamation-triangle-fill"></i>
          {loadError || "Unable to load analytics data. Please try again."}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={refreshData}>Retry</button>
        </div>
      )}

      {loadState === "empty" && (
        <div className="empty-state" role="status">
          <i className="bi bi-bar-chart"></i>
          <h3>No analytics data</h3>
          <p>There is no data for the selected date range or workspace.</p>
        </div>
      )}

      {loadState === "success" && (
        <>
          <section className="metric-grid">
            {visibleMetrics.map((metric) => (
              <MetricCard
                key={metric.id}
                metric={metric}
                onClick={onMetricClick}
              />
            ))}
          </section>

          <section className="analytics-grid">
            <TrendChart range={range} data={analyticsData?.trend || []} />

            <div className="resolution-card">
              <div className="section-heading">
                <div>
                  <h3>AI Resolution</h3>
                  <p>AI handled conversations</p>
                </div>
                <i className="bi bi-three-dots"></i>
              </div>

              <div className="resolution-body">
                <div className="donut">
                  <div className="donut-center">
                    <strong>{analyticsData?.aiResolutionRate ?? 0}%</strong>
                    <span>Resolved</span>
                  </div>
                </div>

                <div className="resolution-legend">
                  <div><span className="legend-dot resolved"></span> AI Resolved <b>{analyticsData?.aiResolutionRate ?? 0}%</b></div>
                  <div><span className="legend-dot handover"></span> Human Handover <b>{100 - (analyticsData?.aiResolutionRate ?? 0)}%</b></div>
                </div>
              </div>
            </div>
          </section>

          {reportMode && (
            <section className="card p-3 mb-3" aria-label="Ads performance report">
              <div className="section-heading"><div><h3>Ads Performance</h3><p>Attribution for tracked campaign and CTA traffic in the selected period.</p></div></div>
              <div className="row g-3">
                <div className="col-md-3"><div className="p-3 border rounded"><small className="text-muted">Impressions</small><div className="fs-4 fw-bold">{Number(analyticsData?.cta?.impressions || 0).toLocaleString()}</div></div></div>
                <div className="col-md-3"><div className="p-3 border rounded"><small className="text-muted">Clicks</small><div className="fs-4 fw-bold">{Number(analyticsData?.cta?.clicks || 0).toLocaleString()}</div></div></div>
                <div className="col-md-3"><div className="p-3 border rounded"><small className="text-muted">CTR</small><div className="fs-4 fw-bold">{analyticsData?.cta?.clickThroughRate || 0}%</div></div></div>
                <div className="col-md-3"><div className="p-3 border rounded"><small className="text-muted">Captured leads</small><div className="fs-4 fw-bold">{Number(analyticsData?.leadConversion?.capturedLeads || 0).toLocaleString()}</div></div></div>
              </div>
            </section>
          )}

          <section className="bottom-grid">
            <div className="table-card">
              <div className="section-heading">
                <div>
                  <h3>Recent Conversations</h3>
                  <p>Latest visitor activity</p>
                </div>
                <button className="link-btn">View All</button>
              </div>

              <div className="table-responsive">
                <table className="table custom-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><div className="user-cell"><span className="avatar a1">R</span> Ramesh</div></td>
                      <td>How can I reset my password?</td>
                      <td><span className="status resolved">Resolved</span></td>
                      <td>10:24 AM</td>
                    </tr>
                    <tr>
                      <td><div className="user-cell"><span className="avatar a2">P</span> Priya</div></td>
                      <td>I need pricing details</td>
                      <td><span className="status progress">In Progress</span></td>
                      <td>10:18 AM</td>
                    </tr>
                    <tr>
                      <td><div className="user-cell"><span className="avatar a3">K</span> Kumar</div></td>
                      <td>Can I get a demo?</td>
                      <td><span className="status resolved">Resolved</span></td>
                      <td>10:05 AM</td>
                    </tr>
                    <tr>
                      <td><div className="user-cell"><span className="avatar a4">S</span> Sneha</div></td>
                      <td>Do you support WhatsApp integration?</td>
                      <td><span className="status progress">In Progress</span></td>
                      <td>09:50 AM</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="sources-card">
              <div className="section-heading">
                <div>
                  <h3>Conversation Sources</h3>
                  <p>Where visitors contacted you</p>
                </div>
              </div>

              <div className="source-row">
                <div className="source-icon website"><i className="bi bi-globe2"></i></div>
                <div className="source-info"><span>Website</span><div className="source-track"><span style={{width:`${analyticsData?.sources?.website ?? 45}%`}}></span></div></div>
                <strong>{analyticsData?.sources?.website ?? 45}%</strong>
              </div>
              <div className="source-row">
                <div className="source-icon whatsapp"><i className="bi bi-whatsapp"></i></div>
                <div className="source-info"><span>WhatsApp</span><div className="source-track"><span style={{width:`${analyticsData?.sources?.whatsapp ?? 30}%`}}></span></div></div>
                <strong>{analyticsData?.sources?.whatsapp ?? 30}%</strong>
              </div>
              <div className="source-row">
                <div className="source-icon facebook"><i className="bi bi-facebook"></i></div>
                <div className="source-info"><span>Facebook</span><div className="source-track"><span style={{width:`${analyticsData?.sources?.facebook ?? 15}%`}}></span></div></div>
                <strong>{analyticsData?.sources?.facebook ?? 15}%</strong>
              </div>
              <div className="source-row">
                <div className="source-icon others"><i className="bi bi-three-dots"></i></div>
                <div className="source-info"><span>Others</span><div className="source-track"><span style={{width:"10%"}}></span></div></div>
                <strong>10%</strong>
              </div>
            </div>
          </section>
        </>
      )}

      {loadState === "loading" && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="spinner-border text-primary" aria-hidden="true"></div>
          <span>Loading analytics...</span>
        </div>
      )}
    </div>
  );
}